/**
 * @file components/audit-tasks/SchedulePanel.tsx
 * @description 稽核任務「預計稽核日期」卡片（客戶 PPT S39 / 序63.4 改期 / 序64）。
 *
 * 2026-09-15 回報序28：本卡片由「文件審查」階段移至「稽核計畫」階段，與稽核排程計劃放在一起；
 * 預定稽核日期不再於派案確認時填寫，改由此處「設定日期」首次填寫，之後可「改期」
 * （序63.4，可新增多筆後續日期並通知管理組）並查看歷史時間線。
 *
 * 功能：
 * - 顯示當前預計稽核日期（取排程時間線最新一筆；未填時提示先於派案確認接收案件）
 * - 「改期 / 設定日期」按鈕：僅任務 assignee 本人可見 →
 *   Modal（DatePicker + 原因 textarea；非首次填寫時原因必填、至少 MIN_RESCHEDULE_REASON_LEN 字）
 * - 歷史時間線（antd Timeline）：每筆顯示日期 / 改期原因 / 操作人 / 操作時間
 *
 * 數據源：
 * - GET  /api/audit-tasks/:taskId/schedules   排程時間線（最新在前）
 * - POST /api/audit-tasks/:taskId/schedules   設定 / 改期（後端同步更新任務快照並通知管理組）
 */

import { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import { CalendarClock } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { MIN_RESCHEDULE_REASON_LEN } from '@zhcb/shared';
import type { AuditScheduleChangeItem, AuditTaskDetail } from '@zhcb/shared';
import { apiClient, ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { message } from '@/lib/antd-app';

interface Props {
  task: AuditTaskDetail;
}

/** Modal 表單值 */
interface ScheduleFormValues {
  plannedDate: Dayjs;
  reason?: string;
}

/** 將 ISO 時間字串格式化為「YYYY-MM-DD HH:mm」 */
function formatDateTime(iso: string): string {
  return dayjs(iso).format('YYYY-MM-DD HH:mm');
}

export function SchedulePanel({ task }: Props) {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<ScheduleFormValues>();

  // 排程時間線（最新在前）；當前預計日期 = 最新一筆的 plannedDate
  const { data, isLoading } = useQuery<{ items: AuditScheduleChangeItem[] }>({
    queryKey: ['audit-task-schedules', task.id],
    queryFn: () =>
      apiClient.get<{ items: AuditScheduleChangeItem[] }>(`/api/audit-tasks/${task.id}/schedules`),
  });

  const items = data?.items ?? [];
  const current = items[0] ?? null;
  /** 已有記錄 → 本次操作為「改期」→ 原因必填 */
  const isReschedule = items.length > 0;
  /** 僅任務 assignee 本人可設定/改期（後端亦強制校驗） */
  const isAssignee = userId != null && userId === task.assigneeId;

  const saveMutation = useMutation({
    mutationFn: (values: ScheduleFormValues) =>
      apiClient.post<{ item: AuditScheduleChangeItem; isReschedule: boolean }>(
        `/api/audit-tasks/${task.id}/schedules`,
        {
          plannedDate: values.plannedDate.format('YYYY-MM-DD'),
          reason: values.reason?.trim() || undefined,
        },
      ),
    onSuccess: (res) => {
      message.success(res.isReschedule ? '已改期並通知管理組' : '預計稽核日期已提交並通知管理組');
      setOpen(false);
      form.resetFields();
      // 刷新時間線 + 任務詳情（plannedAuditDate / plannedAuditMonth 快照已更新）
      void qc.invalidateQueries({ queryKey: ['audit-task-schedules', task.id] });
      void qc.invalidateQueries({ queryKey: ['audit-task-detail', task.id] });
    },
    onError: (err: unknown) => {
      const e = err as ApiError;
      if (e.status === 422) {
        message.error(e.message || `改期必須填寫原因（至少 ${MIN_RESCHEDULE_REASON_LEN} 字）`);
      } else if (e.status === 403) {
        message.error('只有任務稽核員本人可填寫預計稽核日期');
      } else {
        message.error('提交失敗，請稍後重試');
      }
    },
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      saveMutation.mutate(values);
    } catch {
      /* 表單校驗失敗：antd 已在欄位下方展示錯誤，這裡不需額外處理 */
    }
  };

  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <CalendarClock size={14} />
          預計稽核日期
        </Space>
      }
      extra={
        isAssignee && (
          <Button size="small" type="primary" onClick={() => setOpen(true)}>
            {isReschedule ? '改期' : '設定日期'}
          </Button>
        )
      }
      loading={isLoading}
    >
      {/* 當前預計日期 */}
      <div style={{ marginBottom: items.length > 0 ? 16 : 0 }}>
        {current ? (
          <Space size={8}>
            <Tag color="blue" style={{ fontSize: 14, padding: '2px 10px' }}>
              {current.plannedDate}
            </Tag>
            {items.length > 1 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                已改期 {items.length - 1} 次
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">
            尚未填寫。
            {isAssignee
              ? '請點擊右上角「設定日期」填寫預定稽核日期，之後可在此改期。'
              : '待稽核員填寫預定稽核日期。'}
          </Typography.Text>
        )}
      </div>

      {/* 歷史時間線（最新在前）：日期 / 改期原因 / 操作人 / 操作時間 */}
      {items.length > 0 && (
        <Timeline
          items={items.map((it, idx) => ({
            color: idx === 0 ? 'blue' : 'gray',
            children: (
              <Space direction="vertical" size={2}>
                <Space size={8}>
                  <Typography.Text strong>{it.plannedDate}</Typography.Text>
                  <Tag color={it.reason ? 'orange' : 'green'}>
                    {it.reason ? '改期' : '首次填寫'}
                  </Tag>
                </Space>
                {it.reason && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    原因：{it.reason}
                  </Typography.Text>
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {it.createdByName ?? `#${it.createdBy}`} · {formatDateTime(it.createdAt)}
                </Typography.Text>
              </Space>
            ),
          }))}
        />
      )}

      {/* 設定 / 改期 Modal */}
      <Modal
        title={isReschedule ? '改期預計稽核日期' : '設定預計稽核日期'}
        open={open}
        onOk={() => void handleSubmit()}
        confirmLoading={saveMutation.isPending}
        onCancel={() => {
          setOpen(false);
          form.resetFields();
        }}
        okText={isReschedule ? '確認改期' : '提交'}
        destroyOnHidden
      >
        {isReschedule && current && (
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            當前預計日期：{current.plannedDate}。改期將通知管理組，且必須填寫改期原因。
          </Typography.Paragraph>
        )}
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="plannedDate"
            label="預計稽核日期"
            rules={[{ required: true, message: '請選擇預計稽核日期' }]}
          >
            <DatePicker style={{ width: '100%' }} placeholder="選擇具體日期" />
          </Form.Item>
          <Form.Item
            name="reason"
            label={isReschedule ? '改期原因（必填）' : '備註原因（首次填寫可不填）'}
            rules={
              isReschedule
                ? [
                    { required: true, message: '改期必須填寫原因' },
                    {
                      min: MIN_RESCHEDULE_REASON_LEN,
                      message: `原因至少 ${MIN_RESCHEDULE_REASON_LEN} 字`,
                      transform: (v: string) => v?.trim(),
                    },
                  ]
                : []
            }
          >
            <Input.TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder={isReschedule ? '請說明改期原因（至少 5 字）' : '選填'}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
